import AkDropdownMenu, {
  DropdownItemCheckboxGroup as AkDropdownItemCheckboxGroup,
  DropdownItemGroup as AkDropdownItemGroup,
  DropdownItemRadioGroup as AkDropdownItemRadioGroup,
  DropdownMenuGroupProps as AkDropdownMenuGroupProps,
  DropdownMenuProps as AkDropdownMenuProps,
} from '@atlaskit/dropdown-menu';
import { useFlaggingCallback } from '@databases/flags';
import { useStateWithOverride } from '@databases/react';
import React, { ComponentProps, FunctionComponent } from 'react';
import styled from 'styled-components';

export {
  DropdownItem,
  DropdownItemCheckbox,
  DropdownItemRadio,
} from '@atlaskit/dropdown-menu';
export type { CustomTriggerProps, OnOpenChangeArgs } from '@atlaskit/dropdown-menu';

export type DropdownMenuProps = AkDropdownMenuProps;

/**
 * Like AtlasKit DropdownMenu, with the following differences:
 * - Supports the open menu padding
 */
const DropdownMenu: FunctionComponent<DropdownMenuProps> = ({
  isOpen: isOpenProp,
  defaultOpen,
  onOpenChange,
  children,
  ...props
}) => {
  const [isOpen, setIsOpen] = useStateWithOverride(defaultOpen ?? false, isOpenProp);

  const handleOpenChange: NonNullable<AkDropdownMenuProps['onOpenChange']> =
    useFlaggingCallback(
      (args) => {
        setIsOpen(args.isOpen);
        onOpenChange?.(args);
      },
      [onOpenChange, setIsOpen]
    );

  return (
    <AkDropdownMenu isOpen={isOpen} onOpenChange={handleOpenChange} {...props}>
      {children}
    </AkDropdownMenu>
  );
};

export default DropdownMenu;

const StyledDropdownItemGroup = styled(AkDropdownItemGroup)`
  &:first-child {
    border-top: none;
  }
`;

export type DropdownItemGroupProps = AkDropdownMenuGroupProps;

/**
 * Like DropdownItemGroup, but with automatic separators. All dropdown item groups except the first one and
 * except empty ones get a separator.
 */
export const DropdownItemGroup: FunctionComponent<DropdownItemGroupProps> = ({
  ...props
}) => {
  if (React.Children.toArray(props.children).filter((child) => !!child).length === 0) {
    return null;
  } else {
    return <StyledDropdownItemGroup hasSeparator {...props} />;
  }
};

const StyledDropdownItemCheckboxGroup = styled(AkDropdownItemCheckboxGroup)`
  &:first-child {
    border-top: none;
  }
`;

export type DropdownItemCheckboxGroupProps = Omit<
  ComponentProps<typeof AkDropdownItemCheckboxGroup>,
  'hasSeparator'
>;

/**
 * Like DropdownItemCheckboxGroup, but with automatic separators. All dropdown item groups except the first one and
 * except empty ones get a separator.
 */
export const DropdownItemCheckboxGroup: FunctionComponent<
  DropdownItemCheckboxGroupProps
> = ({ ...props }) => {
  if (React.Children.toArray(props.children).filter((child) => !!child).length === 0) {
    return null;
  } else {
    return <StyledDropdownItemCheckboxGroup hasSeparator {...props} />;
  }
};

const StyledDropdownItemRadioGroup = styled(AkDropdownItemRadioGroup)`
  &:first-child {
    border-top: none;
  }
`;

export type DropdownItemRadioGroupProps = Omit<
  ComponentProps<typeof AkDropdownItemRadioGroup>,
  'hasSeparator'
>;

/**
 * Like DropdownItemRadioGroup, but with automatic separators. All dropdown item groups except the first one and
 * except empty ones get a separator.
 */
export const DropdownItemRadioGroup: FunctionComponent<DropdownItemRadioGroupProps> = ({
  ...props
}) => {
  if (React.Children.toArray(props.children).filter((child) => !!child).length === 0) {
    return null;
  } else {
    return <StyledDropdownItemRadioGroup hasSeparator {...props} />;
  }
};
